import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from 'recharts';
import { useQuery } from '@apollo/client';
import { Spin } from 'antd';
import queries from 'graph/queries';
import { getDateFromUTCString } from 'utils/time';

/**
 * Renders a line chart displaying the 5 most recent questionnaire scores.
 */

const ProgressGraph = ({ questionnaireId }) => {
  const { loading, data } = useQuery(queries.questionnaireResponses, {
    variables: { questionnaireId },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const limitedData = useMemo(() => {
    const responses = data?.questionnaireResponses || [];

    const sortedData = [...responses]
      .sort((a, b) => new Date(a.authored) - new Date(b.authored))
      .map((response) => ({
        date: getDateFromUTCString(response.authored).format('YYYY-MM-DD'),
        Score: parseInt(response.scores, 10),
      }));

    // Return only the last 5 items
    return sortedData.slice(-5);
  }, [data?.questionnaireResponses]);

  if (loading && limitedData.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: 350,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <LineChart
        data={limitedData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis />
        <Tooltip />
        <Line type="monotone" dataKey="Score" stroke="#D81F7B" />
      </LineChart>
    </ResponsiveContainer>
  );
};

ProgressGraph.propTypes = {
  questionnaireId: PropTypes.string.isRequired,
};

export default ProgressGraph;
